<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import type { ImpostazioniPromemoria } from '@evolfin/shared';
import { EvolfinApiError } from '@/api/client';
import { useAvvisi } from '@/composables/useAvvisi';
import { useInvalidation } from '@/composables/useInvalidation';
import { settingsKeys } from '../query-keys';
import AvvisoErrore from '@/components/ui/AvvisoErrore.vue';
import CampoCheck from '@/components/ui/CampoCheck.vue';
import CampoSelect from '@/components/ui/CampoSelect.vue';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import StatoCaricamento from '@/components/ui/StatoCaricamento.vue';
import NavImpostazioni from '../components/NavImpostazioni.vue';
import { leggiImpostazioniPromemoria, salvaImpostazioniPromemoria } from '../api';

definePage({
  meta: { titolo: 'Promemoria', privilegi: ['can_manage_promemoria'] },
});

const avvisi = useAvvisi();
const invalidate = useInvalidation('impostazioni');

const { data, isLoading } = useQuery({
  queryKey: settingsKeys.reminders(),
  queryFn: leggiImpostazioniPromemoria,
});

/** Nessun promemoria: `CampoSelect` lavora su stringhe, `''` vale `null`. */
const SPENTO = '';

const GIORNI_SCADENZE = [30, 14, 7, 3, 1, 0];

const OPZIONI_TASK_CON_ORARIO = [
  { valore: SPENTO, etichetta: 'Nessun promemoria' },
  { valore: '15', etichetta: '15 minuti prima' },
  { valore: '30', etichetta: '30 minuti prima' },
  { valore: '60', etichetta: '1 ora prima' },
  { valore: '120', etichetta: '2 ore prima' },
  { valore: '1440', etichetta: '1 giorno prima' },
];

const OPZIONI_TASK_SENZA_ORARIO = [
  { valore: SPENTO, etichetta: 'Nessun promemoria' },
  { valore: '0', etichetta: 'Il giorno stesso' },
  { valore: '1', etichetta: '1 giorno prima' },
  { valore: '2', etichetta: '2 giorni prima' },
  { valore: '3', etichetta: '3 giorni prima' },
];

const giorniScadenze = ref<number[]>([]);
const taskConOrario = ref(SPENTO);
const taskSenzaOrario = ref(SPENTO);

watch(
  data,
  (d) => {
    if (!d) return;
    giorniScadenze.value = [...d.scadenzeGiorniPrima];
    taskConOrario.value = d.taskConOrarioMinutiPrima?.toString() ?? SPENTO;
    taskSenzaOrario.value = d.taskSenzaOrarioGiorniPrima?.toString() ?? SPENTO;
  },
  { immediate: true },
);

/** Le soglie proposte, più quelle salvate che non ne fanno parte. */
const giorniProposti = computed(() =>
  [...new Set([...GIORNI_SCADENZE, ...giorniScadenze.value])].sort((a, b) => b - a),
);

function etichettaGiorni(giorni: number): string {
  if (giorni === 0) return 'Il giorno stesso';
  return giorni === 1 ? '1 giorno prima' : `${giorni} giorni prima`;
}

function imposta(giorni: number, attivo: boolean): void {
  giorniScadenze.value = attivo
    ? [...giorniScadenze.value, giorni]
    : giorniScadenze.value.filter((g) => g !== giorni);
}

const salvataggio = ref(false);
const errore = ref<string | null>(null);

async function salva(): Promise<void> {
  errore.value = null;
  salvataggio.value = true;
  const dati: ImpostazioniPromemoria = {
    scadenzeGiorniPrima: giorniScadenze.value,
    taskConOrarioMinutiPrima: taskConOrario.value === SPENTO ? null : Number(taskConOrario.value),
    taskSenzaOrarioGiorniPrima:
      taskSenzaOrario.value === SPENTO ? null : Number(taskSenzaOrario.value),
  };
  try {
    await salvaImpostazioniPromemoria(dati);
    avvisi.successo('Promemoria salvati');
    await invalidate();
  } catch (e) {
    errore.value = e instanceof EvolfinApiError ? e.message : 'Operazione non riuscita';
  } finally {
    salvataggio.value = false;
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <header>
      <p class="text-xs font-semibold tracking-[0.08em] text-ink-muted uppercase">Impostazioni</p>
      <h1 class="mt-1 font-display text-[26px] leading-tight font-semibold">Promemoria</h1>
      <p class="mt-0.5 text-[13px] text-ink-secondary">
        Valgono per tutto lo studio; dal proprio account ognuno sceglie se riceverli anche via
        email.
      </p>
    </header>

    <NavImpostazioni />

    <StatoCaricamento v-if="isLoading" />

    <form v-else class="flex flex-col gap-5" novalidate @submit.prevent="salva">
      <AvvisoErrore v-if="errore" :messaggio="errore" />

      <section class="card flex flex-col gap-4 p-5">
        <div>
          <h2 class="text-[11px] font-semibold tracking-[0.07em] text-ink-muted uppercase">
            Scadenze di progetto
          </h2>
          <p class="mt-1 text-[12.5px] text-ink-muted">
            Al responsabile, o a tutto il team se la scadenza non ne ha uno.
          </p>
        </div>
        <fieldset class="flex flex-wrap gap-x-5 gap-y-2.5">
          <legend class="sr-only">Quando avvisare</legend>
          <CampoCheck
            v-for="g in giorniProposti"
            :key="g"
            :model-value="giorniScadenze.includes(g)"
            :etichetta="etichettaGiorni(g)"
            @update:model-value="imposta(g, $event)"
          />
        </fieldset>
      </section>

      <section class="card flex flex-col gap-4 p-5">
        <div>
          <h2 class="text-[11px] font-semibold tracking-[0.07em] text-ink-muted uppercase">Task</h2>
          <p class="mt-1 text-[12.5px] text-ink-muted">All'assegnatario, una volta sola.</p>
        </div>
        <div class="grid gap-4 sm:grid-cols-2">
          <CampoSelect
            v-model="taskConOrario"
            etichetta="Con orario"
            :opzioni="OPZIONI_TASK_CON_ORARIO"
          />
          <CampoSelect
            v-model="taskSenzaOrario"
            etichetta="Senza orario"
            :opzioni="OPZIONI_TASK_SENZA_ORARIO"
            nota="Arriva alle 7:30."
          />
        </div>
      </section>

      <div>
        <PulsanteAzione tipo="submit" :caricamento="salvataggio">Salva</PulsanteAzione>
      </div>
    </form>
  </div>
</template>
